import {
    AuditLogChangeType,
    AuditLogDiff,
    HistoryDetailsItem,
} from "@common/types/auditLog";
import { Task, TaskSchema, TaskStatus, TaskType } from "@common/types/task";
import { isRecord } from "../../utils/historyDetailsHelpers";

type TaskSide = "before" | "after";

const FIELD_TITLE = "title";
const FIELD_STATUS = "status";
const FIELD_ASSIGNEE_ID = "assigneeId";
const FIELD_DUE_DATE = "dueDate";
const FIELD_PRIORITY = "priority";
const FIELD_ESTIMATED_TIME = "estimatedTime";
const FIELD_ENTITIES = "entities";
const FIELD_TASK_ENTITY = "taskEntity";
const FIELD_FINANCIAL_DOCUMENTS = "financialDocuments";
const FIELD_TASK_FINANCIAL_DOCUMENT = "taskFinancialDocument";
const FIELD_BLOCKED_BY = "blockedByTasks";
const FIELD_BLOCKING = "blockingTasks";
const FIELD_RELATED = "relatedTasks";

function preferredSide(changeType: AuditLogChangeType): TaskSide {
    return changeType === AuditLogChangeType.DELETE ? "before" : "after";
}

function findDiff(
    diffs: AuditLogDiff[],
    field: string,
): AuditLogDiff | undefined {
    return diffs.find((d) => d.field === field);
}

function readValue(
    diffs: AuditLogDiff[],
    field: string,
    side: TaskSide,
): unknown {
    return findDiff(diffs, field)?.[side]?.value;
}

function readString(
    diffs: AuditLogDiff[],
    field: string,
    side: TaskSide,
): string | null {
    const v = readValue(diffs, field, side);
    return typeof v === "string" ? v : null;
}

function readNumber(
    diffs: AuditLogDiff[],
    field: string,
    side: TaskSide,
): number | null {
    const v = readValue(diffs, field, side);
    if (typeof v === "number") {
        return v;
    }
    if (typeof v === "string") {
        const parsed = Number(v);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

function readArray(
    diffs: AuditLogDiff[],
    field: string,
    side: TaskSide,
): unknown[] {
    const v = readValue(diffs, field, side);
    return Array.isArray(v) ? v : [];
}

function extractNumericId(value: unknown): number | null {
    if (typeof value === "number") {
        return value;
    }
    if (typeof value === "string") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

function readEntityIds(diffs: AuditLogDiff[], side: TaskSide): number[] {
    const fromEntities = readArray(diffs, FIELD_ENTITIES, side);
    const fromTaskEntity = readArray(diffs, FIELD_TASK_ENTITY, side);
    const items = fromEntities.length > 0 ? fromEntities : fromTaskEntity;
    const ids: number[] = [];
    for (const item of items) {
        const direct = extractNumericId(item);
        if (direct !== null) {
            ids.push(direct);
            continue;
        }
        if (isRecord(item)) {
            const rawId = item.entityId ?? item.id;
            const parsed = extractNumericId(rawId);
            if (parsed !== null) {
                ids.push(parsed);
            }
        }
    }
    return ids;
}

function readTaskIds(
    diffs: AuditLogDiff[],
    field: string,
    side: TaskSide,
): string[] {
    const items = readArray(diffs, field, side);
    const ids: string[] = [];
    for (const item of items) {
        if (typeof item === "string") {
            ids.push(item);
            continue;
        }
        if (isRecord(item) && typeof item.id === "string") {
            ids.push(item.id);
        }
    }
    return ids;
}

function readFinancialDocuments(
    diffs: AuditLogDiff[],
    side: TaskSide,
): unknown[] {
    const fromDocuments = readArray(diffs, FIELD_FINANCIAL_DOCUMENTS, side);
    if (fromDocuments.length > 0) {
        return fromDocuments;
    }
    return readArray(diffs, FIELD_TASK_FINANCIAL_DOCUMENT, side);
}

function isTaskStatus(value: unknown): value is TaskStatus {
    return (
        typeof value === "string" &&
        Object.values(TaskStatus).includes(value as TaskStatus)
    );
}

function isTaskType(value: unknown): value is TaskType {
    return (
        typeof value === "string" &&
        Object.values(TaskType).includes(value as TaskType)
    );
}

function resolvePriority(
    diffs: AuditLogDiff[],
    side: TaskSide,
    resource: HistoryDetailsItem["resource"],
): string | null {
    const fromDiff = readValue(diffs, FIELD_PRIORITY, side);
    if (typeof fromDiff === "string") {
        return fromDiff;
    }
    if (isRecord(resource) && typeof resource.priority === "string") {
        return resource.priority;
    }
    return null;
}

function readTaskTypeFromResource(
    resource: HistoryDetailsItem["resource"],
): TaskType {
    if (isRecord(resource) && isTaskType(resource.type)) {
        return resource.type;
    }
    return TaskType.QUESTION;
}

function readResourceField<T>(
    resource: HistoryDetailsItem["resource"],
    field: string,
    guard: (v: unknown) => v is T,
): T | undefined {
    if (!isRecord(resource)) {
        return undefined;
    }
    const v = resource[field];
    return guard(v) ? v : undefined;
}

function isString(v: unknown): v is string {
    return typeof v === "string";
}

function isNumber(v: unknown): v is number {
    return typeof v === "number";
}

function buildCreatedBy(
    log: HistoryDetailsItem,
): Task["createdBy"] | undefined {
    if (isRecord(log.resource)) {
        const fromResource = log.resource.createdBy;
        if (isRecord(fromResource)) {
            return fromResource as Task["createdBy"];
        }
    }
    if (log.actorId == null) {
        return null;
    }
    const name = log.actorName ?? "";
    const [firstName = "", ...rest] = name.split(" ");
    const lastName = rest.join(" ");
    return {
        id: log.actorId,
        firstName,
        lastName,
        iconColor: "#A1A1AA",
        avatar: null,
        name,
        email: null,
    };
}

export function extractTaskSnapshot(log: HistoryDetailsItem): Task | null {
    const side = preferredSide(log.changeType);
    const diffs = log.diff;

    const title =
        readString(diffs, FIELD_TITLE, side) ??
        readResourceField(log.resource, "title", isString) ??
        "(untitled task)";

    const statusRaw =
        readValue(diffs, FIELD_STATUS, side) ??
        (isRecord(log.resource) ? log.resource.status : undefined);
    const status = isTaskStatus(statusRaw) ? statusRaw : TaskStatus.TODO;

    const assigneeId =
        readNumber(diffs, FIELD_ASSIGNEE_ID, side) ??
        readResourceField(log.resource, "assigneeId", isNumber) ??
        null;

    const priority = resolvePriority(diffs, side, log.resource);

    const dueDate =
        readString(diffs, FIELD_DUE_DATE, side) ??
        readResourceField(log.resource, "dueDate", isString) ??
        null;

    const estimatedTime =
        readNumber(diffs, FIELD_ESTIMATED_TIME, side) ??
        readResourceField(log.resource, "estimatedTime", isNumber) ??
        null;

    const entityIds = readEntityIds(diffs, side);
    const financialDocuments = readFinancialDocuments(diffs, side);
    const blockedByTaskIds = readTaskIds(diffs, FIELD_BLOCKED_BY, side);
    const blockingTaskIds = readTaskIds(diffs, FIELD_BLOCKING, side);
    const relatedTaskIds = readTaskIds(diffs, FIELD_RELATED, side);

    const createdBy = buildCreatedBy(log);
    const type = readTaskTypeFromResource(log.resource);

    const candidate: Record<string, unknown> = {
        id: log.resourceId,
        workspaceId: log.scopeId,
        organizationId: null,
        title,
        description: null,
        assigneeId,
        createdById: createdBy?.id ?? null,
        createdBy,
        createdAt: log.createdAt,
        updatedAt: log.createdAt,
        status,
        type,
        priority,
        dueDate,
        estimatedTime,
        entityIds,
        financialDocuments,
        blockedByTaskIds,
        blockingTaskIds,
        relatedTaskIds,
    };

    const result = TaskSchema.safeParse(candidate);
    if (!result.success) {
        return null;
    }
    return result.data;
}
